import {
  ClientProductOption,
  ClientProductSprintTime,
  ClientProductSummary,
  OrganizationClient,
} from '@data/utils/types';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

import type { IError, ListResponse } from '@data';
import * as api from '../api';
import * as queryKeys from '../constants/keys';

export const useListClients = (organizationId?: string): UseQueryResult<ListResponse<OrganizationClient>, IError> =>
  useQuery({
    queryKey: [queryKeys.CLIENTS, organizationId],
    queryFn: () => api.fetchClients(organizationId!),
    enabled: !!organizationId,
  });

export const useListClientProductOptions = (
  organizationId?: string,
  membershipId?: string,
): UseQueryResult<ListResponse<ClientProductOption>, IError> =>
  useQuery({
    queryKey: [queryKeys.CLIENT_PRODUCTS, organizationId, membershipId],
    queryFn: () => api.fetchClientProductOptions(organizationId!, membershipId!),
    enabled: !!organizationId && !!membershipId,
  });

export const useUpdateClientProducts = (
  organizationId: string,
): UseMutationResult<void, IError, { membershipId: string; productIds: string[] }, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, productIds }) => api.updateClientProducts(organizationId, membershipId, productIds),
    // Both sides of the link: the list showing each client's product names, and this client's options.
    onSettled: (_data, _error, { membershipId }) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENTS, organizationId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_PRODUCTS, organizationId, membershipId] });
    },
  });
};

/** The products this client has been given access to — their landing page. */
export const useClientOwnProducts = (
  organizationId?: string,
): UseQueryResult<ListResponse<ClientProductSummary>, IError> =>
  useQuery({
    queryKey: [queryKeys.CLIENT_OWN_PRODUCTS, organizationId],
    queryFn: () => api.fetchClientOwnProducts(organizationId!),
    enabled: !!organizationId,
  });

export const useClientProductTime = (
  organizationId?: string,
  productId?: string,
  params?: { from?: string; to?: string; asClient?: boolean },
): UseQueryResult<ListResponse<ClientProductSprintTime>, IError> =>
  useQuery({
    queryKey: [queryKeys.CLIENT_PRODUCT_TIME, organizationId, productId, params],
    queryFn: () => api.fetchClientProductTime(organizationId!, productId!, params),
    enabled: !!organizationId && !!productId,
  });
